import React from "react";
import { Grid, IconButton } from "@mui/material";
import { AddCircleOutline, DeleteOutline } from "@mui/icons-material";
import { FormikProps } from "formik";
import { InputWithErrorLabel as Input } from "components/haven-design/form/inputs";
import { PricingFormType } from "./form";
import { removePricingTier } from "./pricing-form-utils";

type Props = {
  formik: FormikProps<PricingFormType>;
  monthlyPerUnitHelper: string;
}

const tierFieldWidth = { xs: 12, md: 5 } as const;
const baseRateFieldWidth = { xs: 12, md: 10 } as const;
const tierActionWidth = { xs: "auto" } as const;

const TierActions: React.FC<{
  ariaLabel?: string;
  onRemove?: () => void;
  showAdd?: boolean;
  onAdd?: () => void;
}> = ({ ariaLabel, onRemove, showAdd = false, onAdd }) => (
  <Grid
    size={tierActionWidth}
    sx={{ display: "flex", alignItems: "center", pt: 1, gap: 0.5 }}
  >
    {showAdd ? (
      <IconButton aria-label="Add pricing tier" onClick={onAdd} size="small" color="primary">
        <AddCircleOutline />
      </IconButton>
    ) : null}
    {onRemove ? (
      <IconButton aria-label={ariaLabel} onClick={onRemove} size="small" color="primary">
        <DeleteOutline />
      </IconButton>
    ) : null}
  </Grid>
);

export const PricingTiersFields: React.FC<Props> = ({ formik, monthlyPerUnitHelper }) => {
  const tiers = formik.values.tiers ?? [];

  const addTier = () => {
    void formik.setFieldValue("tiers", [
      ...tiers,
      { lowerThreshold: undefined, monthlyPrice: undefined },
    ]);
  };

  const removeTier = (index: number) => {
    void formik.setFieldValue("tiers", removePricingTier(tiers, index));
  };

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid size={tiers.length > 0 ? tierFieldWidth : baseRateFieldWidth}>
            <Input
              margin="none"
              label={tiers.length > 0 ? "Tier 1 monthly price ($)" : "Monthly price per unit ($)"}
              name="monthlyPricePerUnit"
              type="number"
              inputProps={{ step: "0.01", min: "0" }}
              helperText={monthlyPerUnitHelper}
            />
          </Grid>

          {tiers.length > 0 ? (
            <Grid size={tierFieldWidth}>
              <Input
                margin="none"
                label="Tier 1 cutoff"
                name="tiers.0.lowerThreshold"
                type="number"
                inputProps={{ step: "1", min: "0" }}
                helperText="Units above this count use tier 2 pricing."
              />
            </Grid>
          ) : (
            <TierActions showAdd onAdd={addTier} />
          )}
        </Grid>
      </Grid>

      {tiers.map((_, index) => (
        <Grid key={`pricing-tier-${index}`} size={{ xs: 12 }}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid size={tierFieldWidth}>
              <Input
                margin="none"
                label={`Tier ${index + 2} pricing ($)`}
                name={`tiers.${index}.monthlyPrice`}
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
              />
            </Grid>

            {index + 1 < tiers.length ? (
              <Grid size={tierFieldWidth}>
                <Input
                  margin="none"
                  label={`Tier ${index + 2} cutoff`}
                  name={`tiers.${index + 1}.lowerThreshold`}
                  type="number"
                  inputProps={{ step: "1", min: "0" }}
                  helperText={`Units above this count use tier ${index + 3} pricing.`}
                />
              </Grid>
            ) : null}

            <TierActions
              ariaLabel={`Remove tier ${index + 1}`}
              onRemove={() => removeTier(index)}
              showAdd={index === tiers.length - 1}
              onAdd={addTier}
            />
          </Grid>
        </Grid>
      ))}
    </>
  );
};
